/**
 * Calendari personali condivisi (regole pure, testate in __tests__/sharing.test.ts).
 *
 * Ogni persona ha il suo calendario: le task commerciali assegnate a lei, o create da lei se non hanno
 * un assegnatario. Le righe di `calendar_shares` dicono chi altro lo vede e a che livello.
 * Proprietario e destinatario di una regola possono essere una persona, un reparto o un ruolo;
 * il destinatario anche tutta l'azienda.
 */
export type ShareLevel = 'busy' | 'details' | 'edit';
export type ShareOwnerType = 'user' | 'department' | 'role';
export type ShareSubjectType = 'user' | 'department' | 'role' | 'everyone';

export interface CalendarShare {
  id: string;
  organization_id: string;
  owner_type: ShareOwnerType;
  owner_id: string;
  subject_type: ShareSubjectType;
  subject_id: string | null;
  level: ShareLevel;
}

export interface CalendarPerson { id: string; name: string; roleIds: string[]; departmentIds: string[] }

export const LEVEL_LABELS: Record<ShareLevel, string> = {
  busy: 'Solo occupato',
  details: 'Vede i dettagli',
  edit: 'Può modificare',
};

export const LEVEL_HELP: Record<ShareLevel, string> = {
  busy: 'Vede solo quando la persona è impegnata: un blocco grigio con l\'orario, senza lead né titolo.',
  details: 'Vede gli appuntamenti con lead, tipo e note, ma non li può cambiare.',
  edit: 'Vede tutto e può spostare, riassegnare e dare l\'esito. Per cancellare serve anche il permesso "Cancella le cose dei colleghi".',
};

const RANK: Record<ShareLevel, number> = { busy: 1, details: 2, edit: 3 };

export function maxLevel(a: ShareLevel | undefined, b: ShareLevel | undefined): ShareLevel | undefined {
  if (!a) return b;
  if (!b) return a;
  return RANK[a] >= RANK[b] ? a : b;
}

export function atLeast(level: ShareLevel | undefined, min: ShareLevel): boolean {
  return !!level && RANK[level] >= RANK[min];
}

/** Il calendario a cui appartiene una task commerciale: l'assegnatario, altrimenti chi l'ha creata. */
export function calendarOwnerOf(a: { assigned_to?: string | null; user_id?: string | null }): string | null {
  return a.assigned_to || a.user_id || null;
}

function matches(type: ShareOwnerType | ShareSubjectType, id: string | null, person: CalendarPerson): boolean {
  if (type === 'everyone') return true;
  if (!id) return false;
  if (type === 'user') return person.id === id;
  if (type === 'department') return person.departmentIds.includes(id);
  return person.roleIds.includes(id);
}

/**
 * I calendari che posso vedere, con il livello: il mio sempre in modifica; con "Vede i calendari di tutti"
 * tutti in dettaglio (in modifica con "Modifica le cose dei colleghi"); poi le condivisioni. Vince il livello piu' alto.
 */
export function visibleCalendars(params: {
  meId: string;
  people: readonly CalendarPerson[];
  shares: readonly CalendarShare[];
  canSeeAll: boolean;
  canEditOthers: boolean;
}): Map<string, ShareLevel> {
  const { meId, people, shares, canSeeAll, canEditOthers } = params;
  const me = people.find(p => p.id === meId) ?? { id: meId, name: '', roleIds: [], departmentIds: [] };
  const out = new Map<string, ShareLevel>();
  out.set(meId, 'edit');
  const forMe = shares.filter(s => matches(s.subject_type, s.subject_id, me));
  for (const p of people) {
    if (p.id === meId) continue;
    let level: ShareLevel | undefined = canSeeAll ? (canEditOthers ? 'edit' : 'details') : undefined;
    for (const s of forMe) if (matches(s.owner_type, s.owner_id, p)) level = maxLevel(level, s.level);
    if (level && canEditOthers && level !== 'busy') level = 'edit';
    if (level) out.set(p.id, level);
  }
  return out;
}

/**
 * Calendari accesi alla prima apertura: il mio e quelli che vedo in dettaglio o modifica.
 * Oltre 15 (es. CSE "tutti vedono tutti", 22 colleghi) la settimana diventa illeggibile: si accendono
 * solo quelli delle persone per cui ho fissato appuntamenti di recente (`bookedFor`), come prima.
 * Cosi' un'operatrice parte dai consulenti e un consulente dal suo calendario.
 */
export function defaultShown(visible: ReadonlyMap<string, ShareLevel>, meId: string, bookedFor?: ReadonlySet<string>): Set<string> {
  const rich = [...visible].filter(([id, l]) => id !== meId && atLeast(l, 'details')).map(([id]) => id);
  return new Set([meId, ...(rich.length <= 15 ? rich : rich.filter(id => bookedFor?.has(id)))]);
}

/** Le regole che riguardano il calendario di una persona (le sue, dei suoi reparti e dei suoi ruoli). */
export function sharesOfOwner(shares: readonly CalendarShare[], person: CalendarPerson): CalendarShare[] {
  return shares.filter(s => matches(s.owner_type, s.owner_id, person));
}

/**
 * Come mostrare un elemento a chi guarda. null = non va mostrato.
 * Il calendario è il calendario: si vede solo nel calendario della persona a cui appartiene,
 * se quel calendario mi è visibile e l'ho acceso, al livello di quel calendario.
 */
export function placeActivity(
  a: { assigned_to?: string | null; user_id?: string | null },
  ctx: { meId: string; visible: ReadonlyMap<string, ShareLevel>; shown: ReadonlySet<string> },
): { ownerId: string; level: ShareLevel } | null {
  const ownerId = calendarOwnerOf(a);
  if (!ownerId) return null;
  const level = ctx.visible.get(ownerId);
  return level && ctx.shown.has(ownerId) ? { ownerId, level } : null;
}

/** Testo di una task che chi vede solo "occupato" non deve leggere. */
export const BUSY_TITLE = 'Occupato';

// Colori dei calendari delle persone, assegnati in ordine stabile (alfabetico) e modificabili per sé
export const PERSON_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#06B6D4', '#A855F7', '#E11D48', '#0EA5E9', '#22C55E', '#D946EF'];

export function personColor(personId: string, orderedIds: readonly string[], custom?: Record<string, string> | null): string {
  const c = custom?.[personId];
  if (c && /^#[0-9a-fA-F]{6}$/.test(c)) return c;
  const i = orderedIds.indexOf(personId);
  return PERSON_COLORS[(i < 0 ? 0 : i) % PERSON_COLORS.length];
}

// Colori di base dei tipi di sistema (l'azienda li cambia in Impostazioni › Calendario › Tipi e colori)
export const DEFAULT_TYPE_COLORS: Record<string, string> = {
  videochiamata: '#7C3AED',
  meeting: '#7C3AED',
  appuntamento: '#EA580C',
  call: '#0891B2',
  sopralluogo: '#65A30D',
  email: '#2563EB',
  task: '#6B7280',
  note: '#6B7280',
  other: '#6B7280',
  // Altri elementi del calendario
  evento: '#0EA5E9',
  prenotazione: '#F59E0B',
  task_progetto: '#64748B',
};

export function typeColor(type: string | null | undefined, orgColors?: Record<string, string> | null, customTypeColor?: string | null): string {
  const t = type || 'other';
  return orgColors?.[t] || customTypeColor || DEFAULT_TYPE_COLORS[t] || '#6B7280';
}

export type ColorMode = 'person' | 'type';
